import { LoaderCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom'

import { clearAccessToken, setAccessToken } from '@/features/auth/services/authService.http'
import { ssoErrorKey } from '@/features/auth/lib/ssoErrors'
import { inviteLandingPath, ssoInviteStash } from '@/features/auth/lib/ssoInviteStash'
import { redeemExchangeCode } from '@/features/auth/services/oauthService.http'
import { activeOrgSelected, fetchMe } from '@/features/auth/slices/sessionSlice'
import { useAppDispatch } from '@/store'

/**
 * `/auth/callback` (CR-SSO-INVITE-PX-2026-09-26) — where the server's OAuth
 * callback returns the browser (`px.oauth.frontendCallbackPath`). Before this
 * route existed, SSO sign-in was broken end to end even with real credentials.
 * Adapted from xelerate's `OAuthCallbackPage`.
 *
 * - `#code=` → redeem the single-use code ONCE, keep the access token in
 *   memory, load `/auth/me`, and land on `/` (or, for an invite, in the inviting
 *   org and on its project — TASK-7F3EE34E). The code rides the URL FRAGMENT
 *   (TASK-714FBFBD fix cycle 1b, D-7): a fragment is never sent to a server, so
 *   it reaches no access log and no Referer; leaving with `replace` drops it
 *   from history too.
 * - `?error=` (or a failed redeem) → back to the page the flow started from:
 *   `/accept-invite?token=…&ssoError=…` when the invite stash holds a token,
 *   otherwise `/login?ssoError=…`. Both are client-side navigations, so no
 *   request carries the token; unknown keys become the generic one.
 *
 * Outside AppShell and the guards, in the `/accept-invite` card frame. It has no
 * error UI of its own: every failure goes back to where the person started.
 */
export default function SsoCallbackPage() {
  const { t } = useTranslation('auth')
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const location = useLocation()
  /** StrictMode latch: the exchange code is single-use, so the effect body runs once. */
  const started = useRef(false)

  useEffect(() => {
    if (started.current) return
    started.current = true

    // Always removed on the first render, whatever the outcome.
    const stashed = ssoInviteStash.takeInvite()
    const invite = stashed?.token ?? null
    const back = (raw: string | null): void => {
      const key = encodeURIComponent(ssoErrorKey(raw))
      navigate(
        invite !== null
          ? `/accept-invite?token=${encodeURIComponent(invite)}&ssoError=${key}`
          : `/login?ssoError=${key}`,
        { replace: true }
      )
    }

    const error = params.get('error')
    const code = new URLSearchParams(location.hash.replace(/^#/, '')).get('code')
    if (error !== null && error !== '') {
      back(error)
      return
    }
    if (code === null || code === '') {
      back(null)
      return
    }

    redeemExchangeCode(code)
      .then((accessToken) => {
        setAccessToken(accessToken)
        return dispatch(fetchMe()).unwrap()
      })
      .then(() => {
        // An invite lands in the inviting org, on its project when it has one.
        if (stashed?.orgId !== undefined) dispatch(activeOrgSelected(stashed.orgId))
        navigate(inviteLandingPath(stashed ?? {}), { replace: true })
      })
      .catch(() => {
        // Never leave a half-authenticated session behind.
        clearAccessToken()
        back(null)
      })
  }, [params, location.hash, navigate, dispatch])

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-lg border border-border bg-card px-6 py-6">
        <span className="flex h-8 w-8 items-center justify-center rounded-md bg-primary font-serif text-sm font-bold text-primary-foreground">
          {t('brand.mark')}
        </span>
        <p role="status" className="mt-6 flex items-center gap-2 text-sm text-muted-foreground">
          <LoaderCircle aria-hidden className="size-4 shrink-0 motion-safe:animate-spin" />
          {t('callback.signingIn')}
        </p>
      </div>
    </div>
  )
}
